import { Alert, Skeleton } from '@chakra-ui/react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  DirectUpload,
  useDirectUpload,
} from 'packages/dataPipeline/hooks/useDirectUpload';
import { useLatestMeshDocumentMetadata } from 'packages/dataPipeline/hooks/useLatestMeshDocumentMetadata';
import { useMeshDocument } from 'packages/dataPipeline/hooks/useMeshDocument';
import { useGetMaybeCompleteMeshRunRecordQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { MeshRunParams } from 'packages/dataPipeline/types/dataPipelineTypes';

// monitors the upload process and provides following values.
export type UploadContext = {
  meshRunId: DataPipeline.MeshRunId | null; // meshRunId will be null if the pipeline has not started yet.
  instances: DataPipeline.Instance[]; // instances will be empty if the pipeline has not started yet.
  completeRecord: DataPipeline.CompleteMeshRunRecord | null; // will be null if the pipeline has not started yet.
  canUpload: boolean; // if we can upload a new instance.
  meshDocument: DataPipeline.MeshDocument;
  meshRunParams: MeshRunParams;
};

const uploadContext = createContext<UploadContext | null>(null);

export const useUploadContext = (): UploadContext => {
  const context = useContext(uploadContext);
  finleyAssert(
    context,
    'useUploadMonitor must be used within a uploadContext.',
  );
  return context;
};

// describes sanitization status of a mesh input.
// total: how many sanitize steps are configured.
// success: how many sanitize steps have succeeded.
// failure: how many sanitize steps have failed.
export type Sanitized = {
  total: number;
  success: number;
  failure: number;
};

export const useUploadContextMeshInputs = (): DataPipeline.MeshInput[] => {
  const context = useUploadContext();
  return context.meshDocument.meshInputs;
};

export const useUploadContextMeshDocument = (): DataPipeline.MeshDocument => {
  const context = useUploadContext();
  return context.meshDocument;
};

export const useUploadContextArtifactInstance = (
  artifactId: string,
): {
  instance: DataPipeline.Instance | null;
  directUpload: DirectUpload;
  sanitized: Sanitized;
} => {
  const context = useUploadContext();
  const { meshRunParams } = context;
  const instanceFound: DataPipeline.Instance | null = useMemo(() => {
    return (
      context.instances.find(
        (instance) => instance.artifactId === artifactId,
      ) ?? null
    );
  }, [context.instances, artifactId]);

  // for some tape artifacts we may wait for another artifact
  // for sanitization complete.
  const sanitized: Sanitized = useMemo(() => {
    const foundMeshInput = context.meshDocument.meshInputs.find((meshInput) => {
      return meshInput.artifactId === artifactId;
    });

    finleyAssert(foundMeshInput, `meshInput not found: ${artifactId}`);

    const total = foundMeshInput.sanitizedArtifactIds.length;
    const instances = context.instances.filter((instance) =>
      foundMeshInput.sanitizedArtifactIds.includes(instance.artifactId),
    );

    const success = instances.filter(({ status }) => status === 'success');
    const error = instances.filter(({ status }) => status === 'error');

    return {
      total,
      success: success.length,
      failure: error.length,
    };
  }, [context.meshDocument.meshInputs, context.instances, artifactId]);

  const { directUpload } = useDirectUpload({
    artifactId,
    borrowerOrganizationId: context.meshDocument.borrowerOrganizationId,
    rdfv: meshRunParams.rdfv,
  });

  return {
    directUpload,
    instance: instanceFound,
    sanitized,
  };
};

export const useUploadContextMeshRunParams = (): MeshRunParams => {
  const context = useUploadContext();
  return context.meshRunParams;
};

export const UploadContextProvider = ({
  children,
  meshRunId,
  meshRunParams,
  organizationId,
}: {
  children: React.ReactNode;
  organizationId: string;
  meshRunId: DataPipeline.MeshRunId | null;
  meshRunParams: MeshRunParams | null;
}) => {
  finleyAssert(
    meshRunId || meshRunParams,
    'meshRunId or meshRunParams must be provided',
  );
  const {
    latestMeshDocumentMetadata,
    loading: latestMeshDocumentMetadataLoading,
    error: latestMeshDocumentMetadataError,
  } = useLatestMeshDocumentMetadata({ organizationId });
  const gqlMeshRunParams = meshRunParams
    ? {
        meshDocumentId: meshRunParams.meshDocumentMetadata.meshDocumentId,
        reportingDate: meshRunParams?.rdfv.reportingDate,
        fileVersion: meshRunParams?.rdfv.fileVersion,
      }
    : null;
  const {
    data,
    error: meshRunRecordError,
    startPolling,
    stopPolling,
  } = useGetMaybeCompleteMeshRunRecordQuery({
    variables: {
      meshRunId,
      meshRunParams: gqlMeshRunParams,
    },
  });

  const serializedCompleteMeshRunRecord = data?.maybeCompleteMeshRunRecord;

  // When the complete run record is available it already carries the full mesh
  // document, so `selectedMeshDocument` is unused. Skip the lazy-load fetch (and
  // its loading gate) in that case to avoid an unnecessary request and delay.
  const {
    meshDocument: selectedMeshDocument,
    loading: selectedMeshDocumentLoading,
    error: selectedMeshDocumentError,
  } = useMeshDocument(
    serializedCompleteMeshRunRecord
      ? null
      : meshRunParams?.meshDocumentMetadata.meshDocumentId ?? null,
  );
  const contextValue: UploadContext | null = useMemo(() => {
    if (!serializedCompleteMeshRunRecord) {
      if (!meshRunParams || !selectedMeshDocument) {
        // when hosted on pipeline run details page. meshRunParams will be null
        // we will have to wait for the meshRunParams from server.
        return null;
      }

      const canUpload = !!latestMeshDocumentMetadata.find(
        (latestDoc) =>
          latestDoc.meshDocumentId ===
          meshRunParams.meshDocumentMetadata.meshDocumentId,
      );

      // when hosted on pipeline details page or on home page
      // we do not wait for server, as we know the meshRunParams
      return {
        meshRunId: null,
        completeRecord: null,
        instances: [],
        canUpload,
        meshDocument: selectedMeshDocument,
        meshRunParams,
      };
    }
    const completeRecord = DbGraphUtils.deserializeCompleteMeshRunRecord(
      serializedCompleteMeshRunRecord,
    );

    const { instances } = completeRecord;

    const canUpload = !!latestMeshDocumentMetadata.find(
      (latestDoc) =>
        latestDoc.meshDocumentId === completeRecord.meshDocument.meshDocumentId,
    );

    return {
      completeRecord,
      instances,
      meshRunId: completeRecord.meshRunRecord.meshRunId,
      canUpload,
      meshDocument: completeRecord.meshDocument,
      meshRunParams: {
        meshDocumentMetadata: DataPipeline.toMeshDocumentMetadata(
          completeRecord.meshDocument,
        ),
        rdfv: {
          reportingDate: completeRecord.meshRunRecord.reportingDate,
          fileVersion: completeRecord.meshRunRecord.fileVersion,
        },
      },
    };
  }, [
    latestMeshDocumentMetadata,
    meshRunParams,
    selectedMeshDocument,
    serializedCompleteMeshRunRecord,
  ]);

  const pipelineLastUpdated =
    contextValue?.completeRecord?.meshRunRecord.updatedAt ?? null;

  const pipelineStatus =
    contextValue?.completeRecord?.meshRunRecord.status ?? null;

  const errorMessage =
    latestMeshDocumentMetadataError?.message ??
    selectedMeshDocumentError?.message ??
    meshRunRecordError?.message;

  useEffect(() => {
    // we do not want to poll for older pipelines.
    // here we use lastUpdated to determine if we are going to poll for this graph or not
    // check if the pipeline was last updated less that 2 hours ago
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    const isRecentPipeline =
      !pipelineLastUpdated ||
      Date.now() - pipelineLastUpdated.getTime() < TWO_HOURS;

    const shouldPoll =
      !errorMessage && // do not poll if there is an error fetching the pipeline.
      isRecentPipeline && // do not poll for older pipelines.
      pipelineStatus !== 'success'; // do not poll if pipeline successfully completed.

    if (shouldPoll) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [
    errorMessage,
    pipelineLastUpdated,
    pipelineStatus,
    startPolling,
    stopPolling,
  ]);

  if (errorMessage) {
    return <Alert>LatestDocument Error: {errorMessage} </Alert>;
  }

  if (
    !contextValue ||
    latestMeshDocumentMetadataLoading ||
    selectedMeshDocumentLoading
  ) {
    return <Skeleton width="100%" height="400px" />;
  }

  return (
    <uploadContext.Provider value={contextValue}>
      {contextValue && children}
    </uploadContext.Provider>
  );
};
